<template>
  <div :class="['sidebar', { 'is-open': open, 'h-svh': version != 1 }]" :cols="cols">
    <tag-selector
      v-model="selection"
      :indicators="indicators"
      :app-slot="currentAppSlot"
      :searching="searching"
      @close="close"
      @tag:recovery="recoverTag"
    >
      <sidebar-head
        v-model="currentAppSlot"
        :is-active="searching"
        @search:result="onSearchResult($event)"
        @close="close"
      />
    </tag-selector>
  </div>
</template>

<script>
import isFlipped from '../../mixins/isFlipped'
import SidebarHead from './SidebarHead.vue'
import TagSelector from './TagSelector.vue'
import { sharesNameDecorator } from '../../utils/cube.js';

export default {
  components: { SidebarHead, TagSelector },
  mixins: [isFlipped],
  model: {
    prop: 'appSlot',
    event: 'appslot',
  },
  props: ['open', 'appSlot'],
  data() {
    return {
      indicators: [],
      searching: false,
      appSlotChanged: false,
      selection: {
        firstLevel: null,
        secondLevel: null,
      },
    }
  },
  computed: {
    version() {
      return this.$route.params.version
    },
    cols() {
      return this.indicators.length || this.searching ? 2 : 1
    },
    currentAppSlot: {
      get() {
        return this.appSlot
      },
      set(to) {
        this.appSlotChanged = to !== this.appSlot
        this.$emit('appslot', to)
      },
    },
    mainCubeId() {
      return this.$store.state.query.mainCubeId
    },
    indId: {
      get() {
        return this.$store.state.query[this.appSlot + 'IndId']
      },
    },
    mode() {
      return this.$route.params.mode
    },
    initPath() {
      const indId = this.$store.state.query[this.appSlot + 'IndId']
      if (!indId) return

      const tags = this.$store.getters
        .tagType('firstLevel')
        .filter(t => /database/i.test(t.parent.tagName[this.lng]))
      let path
      for (const tag of tags) {
        const ind = this.$store.getters.tagIndicators(tag.id).find(ind => ind.metaId === this.indId)
        if (ind) {
          path = { tag }
          break
        }
      }

      return path
    },
  },
  watch: {
    appSlot(to, from) {
      this.appSlotChanged = to !== from
    },
    indId(to) {
      if (to && !this.appSlotChanged) this.close()
      this.appSlotChanged = false
    },
    selection(to) {
      this.searching = false
      let indicators
      if (to.secondLevel !== undefined) {
        indicators = this.$store.getters.tagIndicators(to.secondLevel || to.firstLevel) || []
      } else {
        indicators = this.$store.getters.tagIndicators(to.firstLevel) || []
      }

      if (this.isFlipped && this.mode === 'compare') {
        indicators = indicators.filter(ind => ind.cube.cubeMetaId === this.mainCubeId)
      }

      this.indicators = indicators.map(ind => {
        ind.label.en = sharesNameDecorator(ind.label.en, ind.cube.cubeMetaId)
        return ind
      })
    },
    searching(to) {
      if (to) {
        this.selection.firstLevel = null
        this.selection.secondLevel = null
      }
    },
    open(to) {
      if (to && this.initPath) {
        this.selection = { firstLevel: this.initPath.tag.id }
      }
    },
  },
  methods: {
    onSearchResult(evt) {
      let res = evt.res
      let pattern = evt.pattern.toLowerCase()

      let _indicators = res
        .map(ind => this.$store.getters.treeIndicator(ind.id))
        .filter(ind => ind)
        .sort((ind1, ind2) => {
          // sort locally to inlcude indicator with matching names with pattern
          let i1 = ind1.label[this.lng].toLowerCase().includes(pattern)
          let i2 = ind2.label[this.lng].toLowerCase().includes(pattern)
          return i1 < i2 ? 1 : -1
      }).map(ind => {
        ind.label.en = sharesNameDecorator(ind.label.en, ind.cube.cubeMetaId)
        return ind
      })
      this.indicators = _indicators || []
      this.searching = true
    },
    close(closeCol) {
      if (!closeCol) {
        this.$emit('open', false)
        this.appSlotChanged = false
        this.searching = false
      }
      this.selection = { firstLevel: null, secondLevel: null }
      this.indicators = []
    },
    recoverTag(tagId) {
      this.selection = { firstLevel: tagId }
    },
  },
}
</script>

<style lang="stylus">
.sidebar
  position: absolute
  top: 0
  z-index: 12
  width: percentage((1 / 3))
  height: 100%
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.05)
  transition: transform 0.4s ease-in-out
  transform: translate(-100%, 0)
  max-height: calc(100vh - 102px)

  &.h-svh
    max-height: 100svh

  @media screen and (max-width: 1024px)
    max-height: calc(100vh - 64px)

  &:after
    position: absolute
    top: 0
    right: 0
    width: 1px
    height: 100%
    background-color: $gray
    content: ""

  &[cols="2"]
    width: unit((2 / 3) * 100, "vw")

  &[cols="3"]
    width: unit((3 / 3) * 100, "vw")

  .mobile &
    &[cols]
      width: 100vw

    &:after
      background-color: transparent

    +m-breakpoint(landscape)
      width: 50vw

      &[cols="2"]
        width: 100vw

  &.is-open
    transform: translate(0, 0)

.sidebar__wrapper
  position: relative
  height: 100%
  background-color: #fff

.sidebar__body
  position: relative
  display: flex
  flex-direction: column
  height: 100%

  [cols="2"] &
    width: 50%

  .mobile &
    width: 100vw

    +m-breakpoint(landscape)
      width: 50vw

  &:after
    position: absolute
    top: 0
    right: 0
    width: 1px
    height: 100%
    background-color: $gray
    content: ""
</style>
